import { useEffect, useMemo, type ReactElement } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import lockupLight from '../../../../brand/logos/teslyn-lockup-horizontal.svg';
import lockupDark from '../../../../brand/logos/teslyn-lockup-horizontal-dark.svg';
import {
  LEGAL_LABEL,
  formatLegalDate,
  keptQuery,
  legalHref,
  legalText,
  signInHref,
  type LegalDoc,
} from './legal.ts';

const DARK = '(prefers-color-scheme: dark)';

/**
 * The legal pages carry no tenant and no person's theme choice, so they follow the browser's light or dark setting
 * (the portal itself stays light, brand/building-teslyn.md).
 */
function useBrowserTheme(): void {
  useEffect(() => {
    const root = document.documentElement;
    if (typeof window.matchMedia !== 'function') return; // jsdom: stays light
    const mq = window.matchMedia(DARK);
    const apply = (): void => {
      root.dataset.theme = mq.matches ? 'dark' : 'light';
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
}

/** Links to the two legal pages, for the footers of the sign-in and other signed-out pages. */
export function LegalLinks({ search = '' }: { readonly search?: string }): ReactElement {
  return (
    <nav className="legal-links caption" aria-label="Legal">
      <a className="muted" href={legalHref('privacy', search)}>
        {LEGAL_LABEL.privacy}
      </a>
      <a className="muted" href={legalHref('terms', search)}>
        {LEGAL_LABEL.terms}
      </a>
    </nav>
  );
}

export interface LegalPageProps {
  readonly doc: LegalDoc;
  /** The address bar's query; only `?tenant=` is carried to the other links. */
  readonly search?: string;
}

/** `/privacy` or `/terms`: public, no tenant, no session (legal.ts). */
export function LegalPage({ doc, search = '' }: LegalPageProps): ReactElement {
  const text = useMemo(() => legalText(doc), [doc]);
  const other: LegalDoc = doc === 'privacy' ? 'terms' : 'privacy';
  useBrowserTheme();
  useEffect(() => {
    document.title = `${text.title} · Teslyn`;
  }, [text.title]);
  return (
    <div className="legal">
      <header className="legal__head">
        <a href={signInHref(search)} className="legal__home">
          <picture>
            <source srcSet={lockupDark} media={DARK} />
            <img className="legal__logo" src={lockupLight} alt="Teslyn" />
          </picture>
        </a>
        <nav className="legal__nav body" aria-label="Pages">
          <a href={legalHref(other, search)}>{LEGAL_LABEL[other]}</a>
          <a href={signInHref(search)}>Sign in</a>
        </nav>
      </header>
      <main className="legal__main" aria-labelledby="legal-title">
        <article className="legal__text body">
          <h1 id="legal-title" className="title">
            {text.title}
          </h1>
          <p className="legal__meta caption muted">
            Version <span className="data">{text.version}</span> · Last updated:{' '}
            {formatLegalDate(text.updated)}
          </p>
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <h2 className="heading">{children}</h2>,
              h2: ({ children }) => <h2 className="heading">{children}</h2>,
              h3: ({ children }) => <h3 className="body legal__sub">{children}</h3>,
              // links between the pages keep `?tenant=` (preview hosts), as the header's do
              a: ({ href, children }) => (
                <a href={href?.startsWith('/') ? href + keptQuery(search) : href}>{children}</a>
              ),
            }}
          >
            {text.body}
          </Markdown>
        </article>
      </main>
      <footer className="legal__foot caption muted">
        <span>© 2026 Braynewave</span>
        <a href={legalHref(other, search)}>{LEGAL_LABEL[other]}</a>
        <a href={signInHref(search)}>Sign in to Teslyn Cloud</a>
      </footer>
    </div>
  );
}
